import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { resetPassword, setToken, humanizeApiError } from "@/lib/api";

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const token = useMemo(() => sp.get("token") || "", [sp]);
  const navigate = useNavigate();

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const disabled = !token || pw.length < 6 || pw !== pw2 || loading;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    
    if (!token) {
      setErr("Geçersiz bağlantı. Token bulunamadı.");
      return;
    }
    
    if (pw.length < 6) {
      setErr("Şifre en az 6 karakter olmalı.");
      return;
    }
    
    if (pw !== pw2) {
      setErr("Şifreler eşleşmiyor.");
      return;
    }
    
    setErr(null);
    setLoading(true);
    try {
      const res = await resetPassword(token, pw);
      if ((res as any)?.access) setToken((res as any).access); // otomatik giriş
      setOk(true);
      setTimeout(() => navigate("/feed"), 2000);
    } catch (e: any) {
      setErr(humanizeApiError(e?.message || "api_error"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="reset-container">
      <div className="reset-content">
        {/* Logo */}
        <div className="logo-container">
          <img src="/logo/logo.png" alt="SavNot" className="logo-image" />
          <h1 className="app-name">SavNot</h1>
        </div>

        <h2 className="reset-title">Şifreyi Sıfırla</h2>
        
        {!token && (
          <div className="warning-box">
            <div className="warning-icon">⚠️</div>
            <div className="warning-text">Geçersiz bağlantı: token bulunamadı.</div>
            <Link to="/forgot" className="back-button-warning">
              Tekrar Dene
            </Link>
          </div>
        )}

        {ok ? (
          <div className="success-box">
            <div className="success-icon">✅</div>
            <div className="success-title">Şifren güncellendi</div>
            <div className="success-message">
              Akışa yönlendiriliyorsun...
            </div>
          </div>
        ) : token ? (
          <form onSubmit={onSubmit} className="reset-form">
            <p className="reset-description">
              Yeni şifreni belirle. En az 6 karakter olmalı.
            </p>

            {/* New Password */}
            <input
              className="reset-input"
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              minLength={6}
              required
              placeholder="Yeni şifre (en az 6 karakter)"
              autoComplete="new-password"
            />

            {/* Confirm Password */}
            <input
              className="reset-input"
              type="password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              minLength={6}
              required
              placeholder="Yeni şifre (tekrar)"
              autoComplete="new-password"
            />

            {/* Password match warning */}
            {pw && pw2 && pw !== pw2 && (
              <div className="match-warning">
                ⚠️ Şifreler eşleşmiyor
              </div>
            )}

            {/* Error */}
            {err && <p className="reset-error">{err}</p>}

            {/* Submit Button */}
            <button
              type="submit"
              className="reset-button"
              disabled={disabled}
            >
              {loading ? 'Kaydediliyor...' : 'Şifremi Güncelle'}
            </button>

            {/* Back to Login */}
            <Link to="/login" className="login-link">
              ← Giriş Sayfasına Dön
            </Link>
          </form>
        ) : null}
      </div>

      <style>{`
        .reset-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #000;
          padding: 24px;
        }

        .reset-content {
          width: 100%;
          max-width: 420px;
        }

        .logo-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          margin-bottom: 48px;
        }

        .logo-image {
          width: 80px;
          height: 80px;
          object-fit: contain;
        }

        .app-name {
          font-size: 24px;
          font-weight: 800;
          color: #114232;
          margin-top: 8px;
          margin-bottom: 0;
        }

        .reset-title {
          font-size: 28px;
          font-weight: 800;
          color: #fff;
          margin-bottom: 12px;
          text-align: center;
        }

        .reset-description {
          font-size: 14px;
          color: #999;
          text-align: center;
          margin-bottom: 24px;
          line-height: 1.5;
        }

        .reset-form {
          display: flex;
          flex-direction: column;
        }

        .reset-input {
          background-color: #1a1a1a;
          border: 2px solid #114232;
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 16px;
          font-size: 16px;
          color: #fff;
          width: 100%;
          box-sizing: border-box;
        }

        .reset-input::placeholder {
          color: #666;
        }

        .reset-input:focus {
          outline: none;
          border-color: #1a6b4f;
        }

        .match-warning {
          background-color: rgba(255, 193, 7, 0.1);
          border: 1px solid rgba(255, 193, 7, 0.3);
          border-radius: 8px;
          padding: 12px;
          margin-bottom: 12px;
          color: #ffc107;
          font-size: 14px;
          text-align: center;
        }

        .reset-button {
          background-color: #114232;
          border: none;
          border-radius: 12px;
          padding: 16px;
          color: #fff;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          margin-top: 8px;
          width: 100%;
        }

        .reset-button:hover:not(:disabled) {
          background-color: #1a6b4f;
        }

        .reset-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .reset-error {
          color: #ff6b6b;
          text-align: center;
          margin-bottom: 12px;
          font-size: 14px;
          background-color: rgba(255, 107, 107, 0.1);
          padding: 12px;
          border-radius: 8px;
          border: 1px solid rgba(255, 107, 107, 0.2);
        }

        .login-link {
          color: #114232;
          font-size: 14px;
          font-weight: 600;
          text-align: center;
          margin-top: 24px;
          text-decoration: none;
          display: block;
        }

        .login-link:hover {
          text-decoration: underline;
        }

        .warning-box {
          background-color: #1a1a1a;
          border: 2px solid rgba(255, 193, 7, 0.4);
          border-radius: 16px;
          padding: 32px;
          text-align: center;
        }

        .warning-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .warning-text {
          font-size: 16px;
          color: #ffc107;
          margin-bottom: 24px;
        }

        .back-button-warning {
          display: inline-block;
          background-color: #114232;
          border: none;
          border-radius: 12px;
          padding: 12px 24px;
          color: #fff;
          font-size: 14px;
          font-weight: 700;
          text-decoration: none;
          cursor: pointer;
        }

        .back-button-warning:hover {
          background-color: #1a6b4f;
        }

        .success-box {
          background-color: #1a1a1a;
          border: 2px solid #114232;
          border-radius: 16px;
          padding: 32px;
          text-align: center;
        }

        .success-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .success-title {
          font-size: 20px;
          font-weight: 700;
          color: #114232;
          margin-bottom: 12px;
        }

        .success-message {
          font-size: 14px;
          color: #999;
          line-height: 1.6;
        }
      `}</style>
    </div>
  );
}
